const SAMPLE_RATE = 22050
const SAMPLE_LENGTH = 1000

/**
 * Fetches and resolves an array buffer from url
 * @param url url of the audio file
 * @returns array buffer of the file
 */
const fetchArrayBuffer = async (url: string): Promise<ArrayBuffer> => {
  const resp = await fetch(url)
  return await resp.arrayBuffer()
}

/**
 * Decodes an `ArrayBuffer` with lower sample rate using `OfflineAudioContext`
 * @param buffer Audio Buffer that needs to get decoded
 * @returns decoded instance of `AudioBuffer`
 */
const decodeArrayBuffer = async (buffer: ArrayBuffer): Promise<AudioBuffer> => {
  const audioContext = new OfflineAudioContext(1, 1, SAMPLE_RATE)
  return await audioContext.decodeAudioData(buffer)
}

interface LoudnessParams {
  length?: number
  audioBuffer: AudioBuffer
}

/**
 * Gets loudness values from frequency data float array with specified length. Expensive on cpu
 * @param options audio buffer and length (optional)
 */
const getLoudness = (params: LoudnessParams): Float64Array => {
  const { length = SAMPLE_LENGTH, audioBuffer } = params
  const result = new Float64Array(length)
  const array = audioBuffer.getChannelData(0)
  const blockSize = Math.floor(array.length / length)
  for (let i = 0; i < length; i++) {
    const blockStart = blockSize * i
    let sum = 0
    for (let j = 0; j < blockSize; j++) sum = sum + Math.abs(array[blockStart + j])
    result[i] = sum / blockSize
  }
  return result
}

/**
 * Normalizes data from 0 to 1. Expensive on cpu
 * @param array
 * @returns
 */
const normalizeData = (array: Float64Array) => {
  const multiplier = Math.pow(Math.max(...array), -1)
  return array.map((n) => n * multiplier)
}

/**
 *
 * @param url url of the audio file
 * @returns Float64Array of the loudness
 */
export const getLoudnessFromURL = async (url: string): Promise<Float64Array> => {
  const buffer = await fetchArrayBuffer(url)
  const audioBuffer = await decodeArrayBuffer(buffer)
  const rawLoundess = getLoudness({ audioBuffer })
  const normalizedLoudness = normalizeData(rawLoundess)
  return normalizedLoudness
}
